import { useCallback, useMemo, useRef } from 'react';
import { isArray } from 'lodash';

import useToggle from '@lyearn/core/hooks/useToggle';
import { stopEventPropagation } from '@lyearn/core/utils/events';
import { useTranslation } from '@lyearn/i18n';
import { ArticleOutline, BookmarkFill, BookmarkOutline } from '@lyearn/icons';
import { css } from '@lyearn/style';
import { IconButton, Stack, Tooltip, Typography, useSnackbar } from '@lyearn/ui';
import { Image } from '@/components/Image';
import * as SidebarAccordion from '@/components/Panel/SidebarAccordion';
import { useCreateBookmarkMutation } from '@/features/bookmark/graphql/mutations/generated/createBookmark';
import { useDeleteBookmarkMutation } from '@/features/bookmark/graphql/mutations/generated/deleteBookmark';
import { getContentUrl } from '@/modules/corporate/modules/path/utils';
import { convertEnumToReadableString, convertEnumToString } from '@/modules/knowledge-asset/types';
import routes from '@/pages/corporate/routes';
import {
  BookmarkEntityTypeEnum,
  BookmarkTypeEnum,
  KnowledgeAssetSubTypeEnum,
  ReferenceTypeEnum,
  ResourceTypeEnum,
} from '@/types/schema';

import { NoteFragment } from '../../graphql/queries/generated/notes';
import { ChatbotMessageType, ChatbotReferenceType } from '../../types';

const horizontalDivider = css`
  height: 0.1rem;
`;

const getBookmarkEntityType = (
  type: ResourceTypeEnum | ReferenceTypeEnum,
): BookmarkEntityTypeEnum => {
  switch (type) {
    case ReferenceTypeEnum.LessonPage:
    case ReferenceTypeEnum.Lesson:
      return BookmarkEntityTypeEnum.Lesson;
    case ResourceTypeEnum.Course:
      return BookmarkEntityTypeEnum.Course;
    case ResourceTypeEnum.Article:
      return BookmarkEntityTypeEnum.Article;
    default:
      // @ts-ignore
      return type as BookmarkEntityTypeEnum;
  }
};

const getContentTypeStr = (type: any, isIntegration?: boolean) => {
  if (Object.values(KnowledgeAssetSubTypeEnum).includes(type) || isIntegration) {
    return convertEnumToReadableString(type);
  }
  if (type === ReferenceTypeEnum.Lesson || type === ReferenceTypeEnum.LessonPage) {
    return 'Lesson';
  } else {
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  }
};

const getReferenceLink = (content: any): string => {
  if (content.isIntegration) {
    return content.url || '';
  } else if (
    content.type === ReferenceTypeEnum.Lesson ||
    content.type === ReferenceTypeEnum.LessonPage
  ) {
    return `${routes.CourseViewer(content.rootId)}?lessonPageId=${content.id}`;
  } else if (Object.values(KnowledgeAssetSubTypeEnum).includes(content.type)) {
    const type = convertEnumToString(content.type);
    return `/knowledge-asset/${type}/${content.id}/details`;
  } else {
    const { view } = getContentUrl({
      id: content.id,
      type: content.type as ResourceTypeEnum,
    });
    return view;
  }
};

const SourceCards = ({
  references,
  pinnedItems,
  setCurrentNote,
  setHideNoteButtons,
}: {
  references: ChatbotMessageType['references'];
  pinnedItems: any[];
  setCurrentNote?: React.Dispatch<React.SetStateAction<NoteFragment | null>>;
  setHideNoteButtons?: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const [, executeCreateBookmark] = useCreateBookmarkMutation();
  const [, executeDeleteBookmark] = useDeleteBookmarkMutation();
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const [isExpanded, { toggle }] = useToggle(false);

  const onError = useCallback(
    () => showErrorSnackbar({ message: t('Common:Error.SomethingWentWrong') }),
    [showErrorSnackbar],
  );

  const pinnedItemsIds = useMemo(() => pinnedItems.map((item) => item?.entityId), [pinnedItems]);

  const onPin = useCallback(
    (content: any) => {
      const entityId = content.id;
      const entityType = content.type as ResourceTypeEnum;
      const bookmarkEntityType = getBookmarkEntityType(entityType);
      const rootType =
        bookmarkEntityType === BookmarkEntityTypeEnum.Lesson ? ResourceTypeEnum.Course : null;
      executeCreateBookmark({
        input: {
          type: BookmarkTypeEnum.KnowledgeBase,
          entityId,
          entityType: getBookmarkEntityType(entityType),
          ...(content.name && { name: content.name }),
          ...(content.rootId && { parentEntityId: content.rootId }),
          ...(rootType && { parentEntityType: rootType }),
        },
      })
        .then((res) => {
          if (res.data?.createBookmark?.bookmark?.id) {
            showSnackbar({ message: t('Common:KnowledgeBase.BookmarkedSuccessfully') });
          } else {
            onError();
          }
        })
        .catch((error) => {
          onError();
        });
    },
    [executeCreateBookmark, showErrorSnackbar, showSnackbar, t],
  );

  const onUnpin = useCallback(
    (content: any) => {
      const bookmarkId = pinnedItems.find((item) => item?.entityId === content?.id)?.id;
      if (!bookmarkId) {
        return;
      }
      executeDeleteBookmark({ input: { bookmarkId } })
        .then((res) => {
          if (res.data?.deleteBookmark?.bookmark?.id) {
            showSnackbar({ message: t('Common:KnowledgeBase.BookmarkRemovedSuccessfully') });
          } else {
            onError();
          }
        })
        .catch((err) => {
          onError();
        });
    },
    [pinnedItems, executeDeleteBookmark, onError, showSnackbar, t],
  );

  const handleToggle = () => {
    toggle();
    // add later if you need to scroll to bottom on expand
    // setTimeout(() => {
    //   if (bottomRef.current) {
    //     bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    //   }
    // }, 200);
  };

  if (!references || !isArray(references) || !references?.length) {
    return null;
  }

  const sanitizedReferences = references.filter(
    (content: ChatbotReferenceType) => content && Boolean(content.id),
  );

  if (sanitizedReferences.length === 0) {
    return null;
  }

  return (
    <>
      {/* <div className={cn(horizontalDivider, 'my-16 bg-interactive-secondary-hover')} /> */}
      <Stack className="mt-8" direction="col" gapY={8}>
        <Stack
          alignItems="center"
          className="w-fit hover:bg-bg-hover cursor-pointer ps-12 pe-4 py-2 rounded-full -ml-12 text-text-secondary hover:text-text-primary"
          direction="row"
          justifyContent="start"
          onClick={handleToggle}>
          <Typography className="" variant="label-03">
            {t('Common:KnowledgeBase.Sources')}
          </Typography>
          <SidebarAccordion.Chevron className="mt-1" isExpanded={isExpanded} />
        </Stack>

        <SidebarAccordion.Main isExpanded={isExpanded}>
          <Stack className="w-full grid grid-cols-2 xs:grid-cols-1" direction="row" gap={8}>
            {sanitizedReferences.map((content: ChatbotReferenceType) => {
              const isNote = content.type === 'KNOWLEDGE_DOC';

              if (isNote) {
                const mockNoteFragment: NoteFragment = {
                  id: content.parentId || '',
                  title: content.name || '',
                  rootBlockId: content.rootId || '',
                };

                const handleClick = () => {
                  setHideNoteButtons?.(true);
                  setCurrentNote?.(mockNoteFragment);
                };

                return (
                  <Stack
                    key={content.id}
                    alignItems="center"
                    className="w-full p-8 bg-bg-secondary hover:bg-bg-hover rounded-8 border border-border cursor-pointer"
                    justifyContent="between"
                    onClick={handleClick}>
                    <Stack alignItems="center" gapX={10}>
                      <Stack className="flex-shrink-0 p-4">
                        <ArticleOutline fillColorToken="icon-accent" height={32} width={32} />
                      </Stack>
                      <Stack direction="col">
                        <Typography
                          color="text-text-primary"
                          numberOfLines={1}
                          variant="body-short-02">
                          {content.name}
                        </Typography>
                        <Typography color="text-text-secondary" variant="body-short-03">
                          {'Knowledge Doc'}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                );
              }
              const contentUrl = getReferenceLink(content);
              const contentTypeStr = getContentTypeStr(content.type, content.isIntegration);
              const contentTypeLabel =
                contentTypeStr === 'Persona Journey' ? 'Solution Journey' : contentTypeStr;
              const isPinned = content.id && pinnedItemsIds.includes(content.id);

              return (
                <Stack
                  key={content.id}
                  alignItems="center"
                  className="w-full p-8 bg-bg-secondary hover:bg-bg-hover rounded-8 border border-border cursor-pointer"
                  justifyContent="between"
                  onClick={() => window.open(contentUrl, '_blank')}>
                  <Stack alignItems="center" gapX={10}>
                    <Stack className="flex-shrink-0">
                      <Image
                        alt=""
                        className="rounded-4"
                        height={40}
                        src={content.cover.url || ''}
                        width={40}
                      />
                    </Stack>
                    <Stack direction="col">
                      <Typography
                        color="text-text-primary"
                        numberOfLines={1}
                        variant="body-short-02">
                        {content.name}
                      </Typography>
                      <Typography color="text-text-secondary" variant="body-short-03">
                        {contentTypeLabel}
                      </Typography>
                    </Stack>
                  </Stack>
                  {content.isIntegration ? null : (
                    <IconButton
                      className="flex-shrink-0"
                      onClick={(event) => {
                        stopEventPropagation(event);
                        if (isPinned) {
                          onUnpin(content);
                        } else {
                          onPin(content);
                        }
                      }}>
                      <Tooltip
                        title={
                          isPinned
                            ? t('Common:KnowledgeBase.RemoveBookmark')
                            : t('Common:KnowledgeBase.Bookmark')
                        }>
                        {isPinned ? (
                          <BookmarkFill fillColorToken="icon-secondary" height={24} width={24} />
                        ) : (
                          <BookmarkOutline fillColorToken="icon-secondary" height={24} width={24} />
                        )}
                      </Tooltip>
                    </IconButton>
                  )}
                </Stack>
              );
            })}
          </Stack>
        </SidebarAccordion.Main>
        <div ref={bottomRef} />
      </Stack>
    </>
  );
};

export default SourceCards;
